import { FC, useState } from 'react';
import { Button } from '@carbon/react';
import { Edit } from '@carbon/react/icons';

import { FormattedCount } from 'components/formatted-count';

import { EntityPickerPanel } from './entity-picker-panel';
import type { AcquiringEntity } from './types';
import styles from './entity-picker.module.scss';

/** Props for {@link EntityPicker}. */
export interface EntityPickerProps {
  /**
   * Currently-selected acquiring entities. Empty array shows the
   * placeholder; non-empty renders one read-only grid per entity.
   */
  value: AcquiringEntity[];
  /**
   * Called when the user commits a new selection via the slide-over Save.
   * Today the panel is single-pick, so `entity` is one item; the parent
   * decides how to merge it into the existing array (typically: replace).
   */
  onChange: (entity: AcquiringEntity) => void;
}

type StringFieldKey = 'id' | 'entityClassification' | 'entityType';
type CountFieldKey = 'crmTractCount' | 'titleAssetCount' | 'crmAccountCount';

/**
 * Discriminated field spec — `format: 'count'` narrows `key` to the numeric
 * fields so the render path can read `value[key]` as a `number` without a
 * type cast; `format: 'string'` narrows to identifier/label fields.
 */
type DisplayField =
  | { key: StringFieldKey; label: string; format: 'string' }
  | { key: CountFieldKey; label: string; format: 'count' };

const DISPLAY_FIELDS: readonly DisplayField[] = [
  { key: 'id', label: 'Entity ID', format: 'string' },
  { key: 'entityClassification', label: 'Classification', format: 'string' },
  { key: 'entityType', label: 'Type', format: 'string' },
  { key: 'crmTractCount', label: 'CRM Tracts', format: 'count' },
  { key: 'titleAssetCount', label: 'Title Assets', format: 'count' },
  { key: 'crmAccountCount', label: 'CRM Accounts', format: 'count' },
];

/**
 * Inline Acquiring-Entity picker. Renders a label, a name (or placeholder
 * when none), and an Edit button at the top; below, one read-only 2-column
 * grid per item in `value`. The Edit button opens {@link EntityPickerPanel}
 * which is single-pick — the parent's `onChange` decides whether the
 * resulting selection replaces or appends.
 *
 * @param {EntityPickerProps} props - Current value array + commit handler
 * @return {JSX.Element} The entity-picker widget
 */
export const EntityPicker: FC<EntityPickerProps> = ({ value, onChange }) => {
  const [showPanel, setShowPanel] = useState(false);
  const headerName =
    value.length === 0
      ? null
      : value.length === 1
        ? value[0].name
        : `${value[0].name} +${value.length - 1}`;

  return (
    <div className={styles.pickerGroup}>
      <div className={styles.pickerHeader}>
        <span className={styles.pickerLabel}>Acquiring Entity:</span>
        {headerName !== null ? (
          <span className={styles.pickerName}>{headerName}</span>
        ) : (
          <span className={styles.pickerPlaceholder}>
            Select an acquiring entity
          </span>
        )}
        <Button
          kind="ghost"
          hasIconOnly
          renderIcon={Edit}
          iconDescription="Change entity"
          tooltipPosition="bottom"
          tooltipAlignment="center"
          size="sm"
          onClick={() => setShowPanel(true)}
        />
      </div>
      {value.map((entity) => (
        <div key={entity.id} className={styles.pickerReadOnly}>
          {DISPLAY_FIELDS.map((field) => (
            <div key={field.key} className={styles.pickerField}>
              <span className={styles.pickerFieldLabel}>{field.label}</span>
              <span className={styles.pickerFieldValue}>
                {field.format === 'count' ? (
                  <FormattedCount value={entity[field.key]} fallback="—" />
                ) : (
                  (entity[field.key] ?? '—')
                )}
              </span>
            </div>
          ))}
        </div>
      ))}
      <EntityPickerPanel
        open={showPanel}
        selectedEntity={value[0] ?? null}
        onSelect={onChange}
        onClose={() => setShowPanel(false)}
      />
    </div>
  );
};
